// Rumbo: PDF de una página con la llave de repuesto, para guardarlo o
// imprimirlo. Se hace a mano (unas pocas líneas de texto) para no añadir una
// librería de PDF solo para esto. Todo pasa en el aparato: el PDF no se sube.

export type LineaPdf = {
  texto: string;
  tamano: number;
  negrita?: boolean;
  mono?: boolean;
};

// WinAnsi (latin-1 en lo que usamos): acentos, eñes, «», ¿ y ¡. Lo demás se
// cambia por «?» para no romper el PDF.
function escaparTexto(texto: string): string {
  let salida = '';
  for (const caracter of texto.normalize('NFC')) {
    const codigo = caracter.codePointAt(0) ?? 63;
    if (caracter === '(' || caracter === ')' || caracter === '\\') {
      salida += '\\' + caracter;
    } else if (codigo >= 32 && codigo < 127) {
      salida += caracter;
    } else if (codigo >= 160 && codigo <= 255) {
      salida += '\\' + codigo.toString(8).padStart(3, '0');
    } else {
      salida += '?';
    }
  }
  return salida;
}

// Parte una línea larga en varias según un ancho aproximado de caracteres.
export function partirLinea(texto: string, maximo: number): string[] {
  const palabras = texto.split(' ');
  const lineas: string[] = [];
  let actual = '';
  for (const palabra of palabras) {
    if (actual && (actual + ' ' + palabra).length > maximo) {
      lineas.push(actual);
      actual = palabra;
    } else {
      actual = actual ? actual + ' ' + palabra : palabra;
    }
  }
  if (actual) {
    lineas.push(actual);
  }
  return lineas;
}

export function crearPdf(lineas: LineaPdf[]): Uint8Array {
  const ancho = 595;
  const alto = 842;
  const margen = 56;
  let y = alto - margen;
  let contenido = '';
  for (const linea of lineas) {
    // Helvetica a 1 pt mide ~0,5 pt por carácter de media; Courier, 0,6.
    const maximo = Math.floor(
      (ancho - margen * 2) / (linea.tamano * (linea.mono ? 0.6 : 0.5)),
    );
    const fuente = linea.mono ? 'F3' : linea.negrita ? 'F2' : 'F1';
    for (const trozo of linea.texto ? partirLinea(linea.texto, maximo) : ['']) {
      y -= linea.tamano * 1.4;
      contenido += `BT /${fuente} ${linea.tamano} Tf ${margen} ${y.toFixed(1)} Td (${escaparTexto(trozo)}) Tj ET\n`;
    }
  }

  const objetos = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${ancho} ${alto}] /Resources << /Font << /F1 4 0 R /F2 5 0 R /F3 6 0 R >> >> /Contents 7 0 R >>`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Courier-Bold /Encoding /WinAnsiEncoding >>',
    `<< /Length ${contenido.length} >>\nstream\n${contenido}endstream`,
  ];

  let pdf = '%PDF-1.4\n';
  const posiciones: number[] = [];
  objetos.forEach((objeto, i) => {
    posiciones.push(pdf.length);
    pdf += `${i + 1} 0 obj\n${objeto}\nendobj\n`;
  });
  const inicioTabla = pdf.length;
  pdf += `xref\n0 ${objetos.length + 1}\n0000000000 65535 f \n`;
  for (const posicion of posiciones) {
    pdf += `${String(posicion).padStart(10, '0')} 00000 n \n`;
  }
  pdf += `trailer\n<< /Size ${objetos.length + 1} /Root 1 0 R >>\nstartxref\n${inicioTabla}\n%%EOF\n`;

  // Todo es ASCII (lo de fuera va escapado), así que cada carácter es un byte.
  return Uint8Array.from(pdf, caracter => caracter.charCodeAt(0));
}
